import { useEffect, useState, useRef } from "react";

import { CSVLink } from "react-csv";
import { Container } from "@mui/material";
import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import dayjs from "dayjs";

import { MobileDateRangePicker } from "@mui/x-date-pickers-pro/MobileDateRangePicker";
import Alert from "@mui/material/Alert";

import InputLabel from "@mui/material/InputLabel";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import Grid from "@mui/material/Grid";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { ReactComponent as ExpandMoreIcon } from "../../icons/ExpandMore24.svg";
import { ReactComponent as FileDownloadIcon } from "../../icons/FileDownload24.svg";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";
import ProgressBar from "../../components/ProgressBar";

import { doService } from "../../services/ServiceHelper";
import StudentsSelector from "../../components/faculty/report/StudentsSelector";
import { showError, showToast } from "../../components/ATPDialogs";
import ScorePeriodSelector from "../../components/faculty/report/ScorePeriodSelector";
import ScoreCategorySelector from "../../components/faculty/report/ScoreCategorySelector";
import StudentListSelector from "../../components/faculty/report/StudentListSelector";
import TableRowNoResults from "../../components/TableRowNoResults";
import TagSelector from "../../components/faculty/report/TagSelector";

import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import AccordionTitle from "../../components/AccordionTitle";
import IconButton from "@mui/material/IconButton";

// required for chart JS to work properly
import MyVerticalBarChart from "../../components/chart/MyVerticalBarChart";

import {
  searchScores,
  adminUpdateScoreTime,
} from "../../services/ScoreService";
import ReportInfo from "../../components/faculty/report/ReportInfo";
import {
  StripedTableRow,
  StripedTableCell,
} from "../../components/StripedTable";
import { Link } from "react-router-dom";
import UpdateScoreTimeDialog from "../../components/faculty/report/UpdateScoreTimeDialog";

const ScoreReports = () => {
  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();
  const [showProgress, setShowProgress] = useState(false);
  const [_searchPerformed, setSearchPerformed] = useState(false);

  const [_dateRange, setDateRange] = useState([dayjs(), dayjs()]);

  const [_selectedPeriod, setSelectedPeriod] = useState("0");
  const [_selectedScoreCategories, setSelectedScoreCategories] = useState([]);
  const [_selectedTags, setSelectedTags] = useState([]);
  const [_selectedGroup, setSelectedGroup] = useState("S");
  const [_selectedStudents, setSelectedStudents] = useState([]);
  const [_selectedListEid, setSelectedListEid] = useState("XX");
  const [_showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const [_scoreMetrics, setScoreMetrics] = useState([]);
  const [_scoreResults, setScoreResults] = useState([]);
  const [, setStudentMetrics] = useState([]);
  const [, setScoreTotals] = useState([]);
  const [_includeDeleted, setIncludeDeleted] = useState("N");
  const [_includeArchived, setIncludeArchived] = useState("N");

  const [_open, setOpen] = useState(false);

  const [_selectedScore, setSelectedScore] = useState({});
  const [_updatedTime, setUpdatedTime] = useState(null);

  const csvLinkMetrics = useRef();
  const csvLinkScores = useRef();

  const [_tagData, setTagData] = useState({
    series: [{ data: [] }],
  });

  useEffect(() => {}, []);

  const handleChangeDateRange = (value) => setDateRange(value);

  const getSelectedCategoryIds = () => {
    const ids = [];
    for (let cat of _selectedScoreCategories) {
      ids.push(cat.value);
    }
    return ids;
  };

  const getSelectedStudentEids = () => {
    const ids = [];
    for (let student of _selectedStudents) {
      ids.push(student.value);
    }
    return ids;
  };

  const getSelectedTagEids = () => {
    const ids = [];
    for (let tag of _selectedTags) {
      ids.push(tag.value);
    }
    return ids;
  };

  const handleSearch = () => {
    const dateStart = _dateRange[0].format("YYYY-MM-DD");
    const dateEnd = _dateRange[1].format("YYYY-MM-DD");

    if (_selectedScoreCategories.length === 0) {
      showError(
        "Unable to Search",
        "Please select one or more Score Categories.",
      );
      return;
    }

    if (_selectedGroup === "XX") {
      showError("Unable to Search", "Please select a Group.");
      return;
    }
    const categoryIds = getSelectedCategoryIds();
    const studentEids = getSelectedStudentEids();
    const tagEids = getSelectedTagEids();

    setShowProgress(true);
    doService(
      searchScores(
        _school,
        _jwt,
        dateStart,
        dateEnd,
        _selectedPeriod,
        categoryIds,
        _selectedGroup,
        studentEids,
        _selectedListEid,
        _includeDeleted,
        _includeArchived,
        tagEids,
      ),
      (results) => {
        setSearchPerformed(true);

        setScoreMetrics(results.data.scoreMetrics);
        setScoreResults(results.data.scores);
        setStudentMetrics(results.data.studentMetrics);
        setScoreTotals(results.data.totals);

        initTagGraph(results.data.tagCounts);
        setShowProgress(false);
      },
    );
  };

  // map key: t(tagid), value:({count, id, name})
  const initTagGraph = (tagCountsMap) => {
    const labels = [];
    const data = [];

    for (const key in tagCountsMap) {
      const entry = tagCountsMap[key];
      labels.push(entry.name);
      data.push(entry.count);
    }

    setTagData({
      labels: labels,
      series: [
        {
          name: "Tags",
          data: data,
        },
      ],
    });
  };

  const onChangePeriod = (period) => {
    setSelectedPeriod(period);
  };

  const onChangeScoreCategory = (value) => {
    setSelectedScoreCategories(value);
  };

  const onChangeTags = (value) => {
    setSelectedTags(value);
  };

  const handleChangeGroup = (event) => {
    setSelectedGroup(event.target.value);
  };

  const onSelectStudents = (studentEids) => {
    setSelectedStudents(studentEids);
  };

  const onSelectStudentList = (value) => {
    setSelectedListEid(value);
  };

  const handleClickExportMetrics = () => {
    csvLinkMetrics.current.link.click();
  };

  const handleClickExportResults = () => {
    csvLinkScores.current.link.click();
  };

  // const handleClickExportTotals = () => {
  //   csvLinkTotals.current.link.click();
  // };

  const getTeacherName = (score) => {
    if (score.teacherLastName == null) {
      return "N/A";
    }
    return `${score.teacherLastName}, ${score.teacherFirstName.substring(
      0,
      1,
    )}`;
  };

  const getQrFlag = (score) => {
    if (score.comment === "Tardy QR") {
      return "YES";
    }
    return "NO";
  };

  const onChangeIncludeDeleted = (event) => {
    setIncludeDeleted(event.target.value);
  };

  const onChangeIncludeArchived = (event) => {
    setIncludeArchived(event.target.value);
  };

  const handleUpdateTime = (score) => {
    setUpdatedTime(dayjs(score.dateCreated));
    setSelectedScore(score);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onClickOk = () => {
    const newDateTime = _updatedTime.format("YYYY-MM-DD HH:mm:00");

    setShowProgress(true);
    setOpen(false);
    console.log(_selectedScore.eid);
    doService(
      adminUpdateScoreTime(_school, _jwt, _selectedScore.eid, newDateTime),
      (result) => {
        setShowProgress(false);
        showToast("S", "Score Time Updated");
        handleSearch();
      },
    );
  };

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Student Scores" showBack={true} />
        <ProgressBar showProgress={showProgress} />
        <ReportInfo school={_school} jwt={_jwt} />

        <Accordion
          disableGutters
          sx={{ boxShadow: 1, p: 0, mt: 2 }}
          defaultExpanded
        >
          <AccordionSummary
            className="accordionHeader"
            expandIcon={<ExpandMoreIcon />}
          >
            <AccordionTitle text={`Search Criteria`} />
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2} sx={{ mt: 0.0 }}>
              <Grid item xs={6}>
                <InputLabel>Start and End Dates *:</InputLabel>
                <MobileDateRangePicker
                  defaultValue={[dayjs(), dayjs()]}
                  value={_dateRange}
                  sx={{ mt: 1, mb: 0 }}
                  onChange={handleChangeDateRange}
                />
              </Grid>
              <Grid item xs={6}>
                <InputLabel>For Group *:</InputLabel>
                <Select
                  fullWidth
                  size="small"
                  onChange={handleChangeGroup}
                  value={_selectedGroup}
                >
                  <MenuItem key="XX" value="XX">
                    -- SELECT --
                  </MenuItem>
                  <MenuItem key="S" value="S">
                    Student(s)
                  </MenuItem>
                  <MenuItem key="UD" value="UD">
                    Student List (Student Scores)
                  </MenuItem>
                  <MenuItem key="A" value="A">
                    Entire School
                  </MenuItem>
                </Select>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={6} sx={{ mt: 0, pt: 0 }}>
                <ScoreCategorySelector
                  school={_school}
                  jwt={_jwt}
                  onChange={onChangeScoreCategory}
                />
              </Grid>
              <Grid item xs={6}>
                {_selectedGroup === "S" && (
                  <StudentsSelector
                    school={_school}
                    jwt={_jwt}
                    onChange={onSelectStudents}
                    selectedStudents={_selectedStudents}
                  />
                )}
                {_selectedGroup === "UD" && (
                  <StudentListSelector
                    school={_school}
                    jwt={_jwt}
                    onChange={onSelectStudentList}
                  />
                )}
              </Grid>
            </Grid>

            <Link
              component="button"
              variant="body2"
              onClick={() => setShowAdvancedFilters((prev) => !prev)}
              underline="always"
              sx={{ display: "inline-block", mt: 0.5 }}
            >
              {_showAdvancedFilters ? "Hide Advanced" : "Show Advanced"}
            </Link>

            {_showAdvancedFilters && (
              <>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                  <Grid item xs={3} sx={{ mt: 0, pt: 0 }}>
                    <InputLabel>Include Deleted Scores?</InputLabel>
                    <Select
                      fullWidth
                      size="small"
                      onChange={(event) => onChangeIncludeDeleted(event)}
                      value={_includeDeleted}
                    >
                      <MenuItem key="N" value="N">
                        NO
                      </MenuItem>
                      <MenuItem key="Y" value="Y">
                        YES
                      </MenuItem>
                    </Select>
                  </Grid>

                  <Grid item xs={3} sx={{ mt: 0, pt: 0 }}>
                    <InputLabel>Include Archived Scores?</InputLabel>
                    <Select
                      fullWidth
                      size="small"
                      onChange={(event) => onChangeIncludeArchived(event)}
                      value={_includeArchived}
                    >
                      <MenuItem key="N" value="N">
                        NO
                      </MenuItem>
                      <MenuItem key="Y" value="Y">
                        YES
                      </MenuItem>
                    </Select>
                  </Grid>

                  <Grid item xs={6} />
                </Grid>

                <Grid container spacing={2} sx={{ mt: 0 }}>
                  <Grid item xs={6} sx={{ mt: 1 }}>
                    <TagSelector
                      school={_school}
                      jwt={_jwt}
                      onChange={onChangeTags}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ScorePeriodSelector
                      school={_school}
                      onChange={onChangePeriod}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            <Button
              sx={{ mt: 1 }}
              variant="contained"
              fullWidth
              className="button1"
              onClick={handleSearch}
            >
              Search
            </Button>
          </AccordionDetails>
        </Accordion>

        {_searchPerformed && (
          <>
            <Alert severity="info" sx={{ mt: 1 }}>
              Score details are listed below. Click on the header to see the
              results.
            </Alert>

            {_tagData.series[0].data.length > 0 && (
              <>
                <Accordion disableGutters sx={{ boxShadow: 1, p: 0, mt: 2 }}>
                  <AccordionSummary
                    className="accordionHeader"
                    expandIcon={<ExpandMoreIcon />}
                  >
                    <AccordionTitle
                      text={`Tag Results (${_tagData.series[0].data.length})`}
                    />
                  </AccordionSummary>

                  <AccordionDetails>
                    <MyVerticalBarChart data={_tagData} />
                  </AccordionDetails>
                </Accordion>
              </>
            )}

            <Accordion disableGutters sx={{ boxShadow: 1, p: 0, mt: 2 }}>
              <AccordionSummary
                className="accordionHeader"
                expandIcon={<ExpandMoreIcon />}
              >
                <AccordionTitle
                  text={`Score Results (${_scoreResults.length})`}
                />
                <IconButton
                  size="large"
                  color="primary"
                  sx={{ pt: 0.5 }}
                  onClick={handleClickExportResults}
                  title="Export Results"
                >
                  <FileDownloadIcon />
                </IconButton>{" "}
              </AccordionSummary>

              <AccordionDetails>
                <CSVLink
                  data={_scoreResults}
                  filename="score-results.csv"
                  className="hidden"
                  ref={csvLinkScores}
                  target="_blank"
                />

                <TableContainer component={Paper} sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <StripedTableRow>
                        <StripedTableCell align="center">Date</StripedTableCell>
                        <StripedTableCell align="center">Time</StripedTableCell>
                        <StripedTableCell>Student Name</StripedTableCell>
                        <StripedTableCell>Score Category</StripedTableCell>
                        <StripedTableCell align="center">
                          Period
                        </StripedTableCell>
                        <StripedTableCell>Teacher</StripedTableCell>
                        <StripedTableCell>Consequence</StripedTableCell>
                        <StripedTableCell align="center">
                          From QR?
                        </StripedTableCell>
                      </StripedTableRow>
                    </TableHead>
                    <TableBody>
                      <TableRowNoResults data={_scoreResults} />
                      {_scoreResults.map((score) => (
                        <StripedTableRow>
                          <StripedTableCell align="center">
                            {dayjs(score.scoreDate).format("MM/DD")}
                          </StripedTableCell>
                          <StripedTableCell align="center">
                            <Link
                              component="button"
                              onClick={() => handleUpdateTime(score)}
                            >
                              {dayjs(score.dateCreated).format("hh:mm a")}
                            </Link>
                          </StripedTableCell>
                          <StripedTableCell>
                            {score.last_name}, {score.first_name}
                          </StripedTableCell>
                          <StripedTableCell>
                            {score.categoryName}
                          </StripedTableCell>
                          <StripedTableCell align="center">
                            {score.period}
                          </StripedTableCell>
                          <StripedTableCell>
                            {getTeacherName(score)}
                          </StripedTableCell>
                          <StripedTableCell>
                            {score.conValue == null ? "N/A" : score.conValue}
                          </StripedTableCell>
                          <StripedTableCell align="center">
                            {getQrFlag(score)}
                          </StripedTableCell>
                        </StripedTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters sx={{ boxShadow: 1, p: 0, mt: 2 }}>
              <AccordionSummary
                className="accordionHeader"
                expandIcon={<ExpandMoreIcon />}
              >
                <AccordionTitle
                  text={`Score Metrics (${_scoreMetrics.length})`}
                />
                <IconButton
                  size="large"
                  color="primary"
                  sx={{ pt: 0.5 }}
                  onClick={handleClickExportMetrics}
                  title="Export Results"
                >
                  <FileDownloadIcon />
                </IconButton>
              </AccordionSummary>
              <AccordionDetails>
                <CSVLink
                  data={_scoreMetrics}
                  filename="score-metrics.csv"
                  className="hidden"
                  ref={csvLinkMetrics}
                  target="_blank"
                />

                <TableContainer component={Paper} sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <StripedTableRow>
                        <StripedTableCell>Score Category</StripedTableCell>
                        <StripedTableCell>High Value</StripedTableCell>
                        <StripedTableCell>Low Value</StripedTableCell>
                        <StripedTableCell>Average</StripedTableCell>
                      </StripedTableRow>
                    </TableHead>
                    <TableBody>
                      <TableRowNoResults data={_scoreMetrics} />
                      {_scoreMetrics.map((score) => (
                        <StripedTableRow>
                          <StripedTableCell>
                            {score.categoryName}
                          </StripedTableCell>
                          <StripedTableCell>
                            {score.maxScoreValue}
                          </StripedTableCell>
                          <StripedTableCell>
                            {score.minScoreValue}
                          </StripedTableCell>
                          <StripedTableCell>
                            {score.avgScoreValue}
                          </StripedTableCell>
                        </StripedTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Container>

      {_open && (
        <UpdateScoreTimeDialog
          value={_updatedTime}
          onChange={(djValue) => {
            setUpdatedTime(djValue);
          }}
          onClose={handleClose}
          onOk={onClickOk}
        />
      )}
    </>
  );
};

export default ScoreReports;
